/**
 * Échéances réglementaires d'une machine (CDC §6.4).
 *
 * Le rattachement d'une règle n'est jamais automatique — ESP ne concerne pas
 * tous les équipements sous pression, F-Gaz pas toutes les machines
 * frigorifiques — c'est ici qu'on le décide, une règle à la fois. Les
 * échéances qui en découlent sont calculées par le serveur ; l'écran se
 * contente d'afficher leur urgence et de permettre de les solder.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';

import {
  ApiError,
  api,
  type ComplianceObligation,
  type ComplianceOutcome,
  type ComplianceRule,
  type ComplianceRuleAssignment,
  type PieceJointe,
} from '../api/client';
import { RESULTAT_OBLIGATION, URGENCE_OBLIGATION } from '../domaine/conformite';
import { ZoneDepot } from './ZoneDepot';

export function Conformite({ assetId }: { assetId: string }) {
  const [regles, setRegles] = useState<ComplianceRuleAssignment[]>([]);
  const [disponibles, setDisponibles] = useState<ComplianceRule[]>([]);
  const [obligations, setObligations] = useState<ComplianceObligation[]>([]);
  const [choix, setChoix] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);
  const [realisationOuverte, setRealisationOuverte] = useState<string | null>(null);

  const charger = useCallback(async () => {
    try {
      const [r, d, o] = await Promise.all([
        api.assetComplianceRules(assetId),
        api.complianceRules(false),
        api.assetComplianceObligations(assetId),
      ]);
      setRegles(r);
      setDisponibles(d);
      setObligations(o);
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Échéances illisibles.');
    } finally {
      setChargement(false);
    }
  }, [assetId]);

  useEffect(() => {
    void charger();
  }, [charger]);

  const rattacher = async () => {
    if (!choix) return;
    setErreur(null);
    try {
      await api.assignComplianceRule(assetId, choix);
      setChoix('');
      await charger();
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Rattachement impossible.');
    }
  };

  const detacher = async (rattachement: ComplianceRuleAssignment) => {
    if (!window.confirm(`Détacher « ${rattachement.ruleLabel} » de cette machine ?`)) return;
    setErreur(null);
    try {
      await api.unassignComplianceRule(assetId, rattachement.ruleId);
      await charger();
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Retrait impossible.');
    }
  };

  // Une règle déjà rattachée ne se propose plus au rattachement.
  const proposables = disponibles.filter((r) => !regles.some((x) => x.ruleId === r.id));

  return (
    <div className="carte">
      <h2>Échéances réglementaires</h2>

      {erreur && <div className="message erreur">{erreur}</div>}

      <div className="carte-corps">
        <div className="regles-rattachees">
          {regles.length === 0 ? (
            <div className="aide">Aucune règle rattachée à cette machine.</div>
          ) : (
            <ul className="liste-regles-rattachees">
              {regles.map((r) => (
                <li key={r.id}>
                  <span>{r.ruleLabel}</span>
                  <button
                    className="secondaire discret danger-discret"
                    onClick={() => void detacher(r)}
                  >
                    Détacher
                  </button>
                </li>
              ))}
            </ul>
          )}

          {proposables.length > 0 && (
            <div className="saisie-ligne">
              <select
                value={choix}
                onChange={(e) => setChoix(e.target.value)}
                aria-label="Règle à rattacher"
              >
                <option value="">Rattacher une règle…</option>
                {proposables.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.label}
                  </option>
                ))}
              </select>
              <button
                className="secondaire discret"
                disabled={!choix}
                onClick={() => void rattacher()}
              >
                Rattacher
              </button>
            </div>
          )}
        </div>

        {chargement ? (
          <div className="vide">Chargement…</div>
        ) : obligations.length === 0 ? (
          <div className="vide">
            Aucune échéance. Rattachez une règle publiée : ses jalons calculeront automatiquement
            les prochaines dates.
          </div>
        ) : (
          <ul className="liste-obligations">
            {obligations.map((o) => (
              <BlocObligation
                key={o.id}
                obligation={o}
                ouvert={realisationOuverte === o.id}
                onBasculer={() =>
                  setRealisationOuverte((v) => (v === o.id ? null : o.id))
                }
                onEnregistre={() => {
                  setRealisationOuverte(null);
                  void charger();
                }}
                onErreur={setErreur}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function BlocObligation({
  obligation,
  ouvert,
  onBasculer,
  onEnregistre,
  onErreur,
}: {
  obligation: ComplianceObligation;
  ouvert: boolean;
  onBasculer: () => void;
  onEnregistre: () => void;
  onErreur: (m: string | null) => void;
}) {
  const etat = URGENCE_OBLIGATION[obligation.urgency];
  const realisee = obligation.urgency === 'DONE';
  const [documentsOuverts, setDocumentsOuverts] = useState(false);

  return (
    <li className="obligation">
      <div className="obligation-entete">
        <div>
          <strong>{obligation.label}</strong>
          <div className="aide">
            {obligation.ruleLabel} · échéance le {jour(obligation.dueDate)}
          </div>
        </div>
        <div className="actions">
          <span className={`etat ${etat.classe}`}>
            {realisee && obligation.completedAt
              ? `Réalisée le ${jour(obligation.completedAt)}${
                  obligation.completedOutcome
                    ? ` — ${RESULTAT_OBLIGATION[obligation.completedOutcome]}`
                    : ''
                }`
              : etat.texte}
          </span>
          <button
            className="secondaire discret"
            onClick={() => setDocumentsOuverts((v) => !v)}
          >
            {documentsOuverts ? 'Masquer les documents' : 'Documents'}
          </button>
          {!realisee && (
            <button className="secondaire discret" onClick={onBasculer}>
              {ouvert ? 'Annuler' : 'Marquer réalisée'}
            </button>
          )}
        </div>
      </div>

      {obligation.completedNote && <div className="aide">{obligation.completedNote}</div>}

      {ouvert && !realisee && (
        <FormulaireRealisation
          obligation={obligation}
          onEnregistre={onEnregistre}
          onErreur={onErreur}
        />
      )}

      {documentsOuverts && <DocumentsObligation obligationId={obligation.id} />}
    </li>
  );
}

/**
 * Rapport de l'organisme (Apave, frigoriste…) rattaché à une échéance.
 *
 * Volontairement plus compact que `PiecesJointes` (galerie, historique,
 * classement) : ici il ne s'agit que de retrouver la preuve d'une
 * intervention, avant ou après l'avoir marquée réalisée — sans lien entre
 * les deux actions.
 */
function DocumentsObligation({ obligationId }: { obligationId: string }) {
  const [pieces, setPieces] = useState<PieceJointe[]>([]);
  const [chargement, setChargement] = useState(true);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const charger = useCallback(async () => {
    try {
      setPieces(await api.files('compliance_obligation', obligationId));
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Documents illisibles.');
    } finally {
      setChargement(false);
    }
  }, [obligationId]);

  useEffect(() => {
    void charger();
  }, [charger]);

  const envoyer = async (fichiers: readonly File[]) => {
    if (fichiers.length === 0) return;
    setErreur(null);
    setEnCours(true);
    try {
      for (const fichier of fichiers) {
        await api.uploadFile('compliance_obligation', obligationId, fichier);
      }
      await charger();
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Envoi impossible.');
    } finally {
      setEnCours(false);
    }
  };

  const ouvrir = async (piece: PieceJointe) => {
    try {
      const { url } = await api.fileUrl(piece.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Ouverture impossible.');
    }
  };

  const retirer = async (piece: PieceJointe) => {
    if (!window.confirm(`Retirer « ${piece.originalName} » ?`)) return;
    setErreur(null);
    try {
      await api.archiveFile(piece.id);
      await charger();
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Retrait impossible.');
    }
  };

  return (
    <div className="obligation-documents">
      {erreur && <div className="message erreur">{erreur}</div>}
      {chargement ? (
        <div className="aide">Chargement…</div>
      ) : pieces.length === 0 ? (
        <div className="aide">Aucun document. Le rapport de l’organisme se dépose ici.</div>
      ) : (
        <ul className="liste-documents-obligation">
          {pieces.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                className="bouton-lien discret"
                onClick={() => void ouvrir(p)}
              >
                {p.originalName}
              </button>
              <span className="aide">{Math.round(p.byteSize / 1024)} Ko</span>
              <button
                type="button"
                className="secondaire discret danger-discret"
                onClick={() => void retirer(p)}
              >
                Retirer
              </button>
            </li>
          ))}
        </ul>
      )}
      <ZoneDepot
        compacte
        multiple
        accept="application/pdf,image/jpeg,image/png"
        desactive={enCours}
        libelle={enCours ? 'Envoi en cours…' : 'Glissez le rapport ici, ou'}
        boutonLibelle="Joindre un document"
        onFichiers={(fichiers) => void envoyer(fichiers)}
      />
    </div>
  );
}

/** Solder une échéance : une date, un résultat, rien d'autre au quotidien. */
function FormulaireRealisation({
  obligation,
  onEnregistre,
  onErreur,
}: {
  obligation: ComplianceObligation;
  onEnregistre: () => void;
  onErreur: (m: string | null) => void;
}) {
  const [completedAt, setCompletedAt] = useState(aujourdHui());
  const [outcome, setOutcome] = useState<ComplianceOutcome>('OK');
  const [note, setNote] = useState('');
  const [enCours, setEnCours] = useState(false);

  const soumettre = async (e: FormEvent) => {
    e.preventDefault();
    setEnCours(true);
    onErreur(null);
    try {
      await api.completeComplianceObligation(obligation.id, {
        // Date de calendrier telle quelle : le serveur la traite comme un
        // jour, jamais un instant précis (comme l'échéance elle-même). Lui
        // ajouter une heure convertie en UTC a déjà produit un faux « futur ».
        completedAt,
        outcome,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      onEnregistre();
    } catch (err) {
      onErreur(err instanceof ApiError ? err.message : 'Enregistrement impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <form className="saisie-ligne saisie-realisation" onSubmit={(e) => void soumettre(e)}>
      <input
        type="date"
        value={completedAt}
        max={aujourdHui()}
        onChange={(e) => setCompletedAt(e.target.value)}
        aria-label="Date de réalisation"
      />
      <select
        value={outcome}
        onChange={(e) => setOutcome(e.target.value as ComplianceOutcome)}
        aria-label="Résultat"
      >
        {(Object.keys(RESULTAT_OBLIGATION) as ComplianceOutcome[]).map((o) => (
          <option key={o} value={o}>
            {RESULTAT_OBLIGATION[o]}
          </option>
        ))}
      </select>
      <input
        type="text"
        placeholder="Note (facultatif)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        aria-label="Note"
      />
      <button disabled={enCours}>{enCours ? 'Enregistrement…' : 'Confirmer'}</button>
    </form>
  );
}

function jour(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR');
}

function aujourdHui(): string {
  const d = new Date();
  const mois = String(d.getMonth() + 1).padStart(2, '0');
  const jourDuMois = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mois}-${jourDuMois}`;
}
